import { Box, Typography, Stack } from '@mui/material'
import { C1IconButton } from 'c1ui'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { C1Icon } from 'components/core/icons/C1Icon'
import { CloudUpload, Paperclip, X } from 'lucide-react'
import { FC, useCallback, useState, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

export interface C1FileInputProps {
    label: string
    name: string
    value?: File
    onChange: (value: File | undefined) => void
    onBlur?: () => void
    error?: boolean
    helperText?: string
    disabled?: boolean
    acceptedFileTypes?: string[]
}

export const C1FileInput: FC<C1FileInputProps> = ({
    label,
    name,
    value,
    onChange,
    onBlur,
    helperText,
    disabled = false,
    acceptedFileTypes = [],
}) => {
    const [dragActive, setDragActive] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const intl = useIntl()

    const handleFile = useCallback((file: File): void => {
        onChange(file)
        onBlur?.()
    }, [onChange, onBlur])

    const handleClear = useCallback((): void => {
        onChange(undefined)
        onBlur?.()
        // Clear the file input element
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }, [onChange, onBlur])

    const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
        const files = e.target.files
        if (files && files.length > 0) handleFile(files[0])
    }, [handleFile])

    const handleDragOver = useCallback((e: React.DragEvent): void => {
        e.preventDefault()
        setDragActive(true)
    }, [])

    const handleDragLeave = useCallback((e: React.DragEvent): void => {
        e.preventDefault()
        setDragActive(false)
    }, [])

    const handleDrop = useCallback((e: React.DragEvent): void => {
        e.preventDefault()
        e.stopPropagation()
        setDragActive(false)

        if (disabled) return

        const files: File[] = []

        if (e.dataTransfer.items) {
            const items = e.dataTransfer.items

            for (let i = 0; i < items.length; i++) {
                if (items[i].kind === 'file') {
                    const file = items[i].getAsFile()
                    if (file) files.push(file)
                }
            }
        } else {
            for (let i = 0; i < e.dataTransfer.files.length; i++) {
                const file = e.dataTransfer.files[i]
                files.push(file)
            }
        }

        if (files.length > 0) handleFile(files[0])
    }, [disabled, handleFile])

    const getAcceptString = (): string => (acceptedFileTypes.length === 0 ? '*/*' : acceptedFileTypes.join(','))

    return (
        <Box>
            <Box mb={1}>
                <Typography>{label}</Typography>
                {helperText && <Typography color="text.secondary">{helperText}</Typography>}
            </Box>

            <Box
                sx={{
                    p: 2,
                    borderWidth: 1,
                    borderStyle: value ? 'solid' : 'dashed',
                    borderColor: (value || dragActive) ? 'primary.main' : 'border.emphasis',
                    borderRadius: C1BorderRadius.large,
                    backgroundColor: dragActive ? 'primary.extraLight' : 'background.default',
                    cursor: disabled ? 'default' : 'pointer',

                    '&:hover': {
                        borderColor: disabled ? 'border.emphasis' : 'primary.main',
                        backgroundColor: disabled ? 'background.default' : 'primary.extraLight',
                    },
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={(): void => {
                    if (!disabled) fileInputRef.current?.click()
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                >
                    {value ? <>
                        <C1Icon icon={Paperclip} sx={{ color: 'primary.main' }} />
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {value.name}
                            </Typography>
                            <Typography color="text.secondary" variant="caption">
                                {`${(value.size / 1024).toFixed(1)} KB`}
                            </Typography>
                        </Box>
                        <C1IconButton
                            variant="text"
                            aria-label={intl.formatMessage({ id: 'file_field_clear', defaultMessage: 'Clear' })}
                            onClick={(e): void => {
                                e.stopPropagation()
                                handleClear()
                            }}
                            disabled={disabled}
                        >
                            <C1Icon icon={X} />
                        </C1IconButton>
                    </> : <>
                        <C1Icon icon={CloudUpload} sx={{ color: 'text.secondary' }} />
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography color="text.secondary" variant="body2">
                                <FormattedMessage
                                    id="file_field_drag_drop_text"
                                    defaultMessage="Drag and drop a file here, or click to select"
                                />
                            </Typography>
                            {acceptedFileTypes.length > 0 && (
                                <Typography color="text.secondary" variant="caption">
                                    <FormattedMessage
                                        id="file_field_accepted_types"
                                        defaultMessage="Accepted types: {types}"
                                        values={{ types: acceptedFileTypes.join(', ') }}
                                    />
                                </Typography>
                            )}
                        </Box>
                    </>}
                </Stack>
            </Box>

            <input
                ref={fileInputRef}
                type="file"
                name={name}
                accept={getAcceptString()}
                onChange={handleFileInput}
                onBlur={onBlur}
                style={{ display: 'none' }}
                disabled={disabled}
            />
        </Box>
    )
}
